import React, { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import {
  DropDownList,
  type DropDownListChangeEvent,
  type DropDownListHandle,
} from "@progress/kendo-react-dropdowns";
import { t } from "i18next";

export interface UniDropdownOption {
  label: string;
  subLabel?: string;
  value: string;
}

interface UniDropdownProps {
  fieldName?: string;
  label?: string;
  value: string | null;
  // Fixed: consumers (e.g. ZohoErp) set string state from this, so it must
  // receive the selected string value, not the raw Kendo change event.
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  placeholder?: string;
  // Accepts either a plain string list (existing usages, unchanged) or an
  // array of {label, subLabel?, value} for a two-line option (e.g. Base URL:
  // gxLookupCode on top, gxLookupValue small underneath).
  data: string[] | UniDropdownOption[];
  validationMessage?: string;
  showStarLabel?: boolean;
  disable?: boolean;
  iconSrc?: string;
  triggerValidation?: boolean;
  tooltip?: string;
  tooltipFloat?: "up" | "down";
}

const isOptionArray = (
  data: string[] | UniDropdownOption[],
): data is UniDropdownOption[] => data.length > 0 && typeof data[0] === "object";

export const UniDropdown = ({
  label = "",
  value,
  onChange,
  onValidate,
  placeholder = "Select Option",
  data,
  showStarLabel = true,
  disable = false,
  iconSrc,
  triggerValidation,
  fieldName = "uniDropdown",
  validationMessage,
  tooltip,
  tooltipFloat = "up",
}: UniDropdownProps) => {
  const dropdownRef = useRef<DropDownListHandle>(null);
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const selectedValue = value ?? "";
  const [showTooltip, setShowTooltip] = useState(false);

  // Normalize plain string[] into the same option shape so the rest of the
  // component only ever deals with one type. Existing callers that pass
  // string[] are unaffected — they just get label === value, no subLabel.
  const normalizedOptions: UniDropdownOption[] = isOptionArray(data)
    ? data
    : (data as string[]).map((d) => ({ label: d, value: d }));

  // Look the current value up in the *current* options list instead of
  // trusting it blindly. If the value isn't in data (stale value, options
  // still loading, mismatched API field names, etc.) we pass null to Kendo
  // rather than a dangling/unmatched value — this is what was crashing
  // DropDownList with "Cannot read properties of undefined (reading
  // 'disabled')" before.
  const selectedItem =
    normalizedOptions.find((opt) => opt.value === selectedValue) ?? null;

  // Validation rules
  const validate = (val: string): string => {
    const trimmed = val.trim();
    if (!trimmed) return `${t("please_select_the")} ${label}.`;
    return "";
  };

  const runValidation = (val: string) => {
    const msg = validate(val);
    setError(msg);
    onValidate?.(fieldName, msg === "");
    return msg;
  };

  const handleFocus = () => setFocused(true);

  const handleBlur = () => {
    setFocused(false);
    setTouched(true);
    runValidation(selectedValue);
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      runValidation(selectedValue);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    }
  }, [validationMessage]);

  const InfoIcon = tooltip ? (
    <div
      className="gx_info_icon_wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <img src={`${ICON_BASE_PATH}infoS.svg`} alt="Info Icon" />
      {showTooltip && (
        <div
          className={`gx-uni-tooltip ${
            tooltipFloat === "down" ? "gx_tooltip_down" : "gx_tooltip_up"
          }`}
        >
          <p className="gx-p12-r">{tooltip}</p>
          <img
            src={`${ICON_BASE_PATH}tooltiptriangle.svg`}
            alt="Tooltip arrow"
            className="tooltip-arrow"
          />
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="gx-dropdown relative">
      <div
        className={`datepicker-field-wrapper ${
          touched && error ? "invalid" : ""
        }`}
      >
        <label
          className={`input-label gx-font-semibold ${
            focused || selectedValue ? "floating" : ""
          }`}
          onClick={() => dropdownRef.current?.focus()}
        >
          {label || placeholder}
          {showStarLabel && (
            <span
              className={`gx_input_star ${
                focused || selectedValue
                  ? "gx_input_star_float"
                  : "gx_input_star_normal"
              }`}
            >
              *
            </span>
          )}
          {/* Info icon floats with label */}
          {(focused || selectedValue) && InfoIcon}
        </label>

        <div
          className={`gx-dropdown-container ${
            touched && error ? "gx-dropdown-invalid" : "gx-dropdown-valid"
          }`}
        >
          {iconSrc ? (
            <img
              src={`${ICON_BASE_PATH}${iconSrc}`}
              alt="icon"
              className="gx-dropdown-icon-left"
            />
          ) : (
            <img
              src={`${ICON_BASE_PATH}state.svg`}
              alt="Dropdown Icon"
              className="gx-dropdown-icon-left"
            />
          )}

          <DropDownList
            ref={dropdownRef}
            data={normalizedOptions}
            textField="label"
            dataItemKey="value"
            value={selectedItem}
            onChange={(e: DropDownListChangeEvent) => {
              // Fixed: pass the option's `value` string up to the parent,
              // not the raw Kendo change event / option object - previously
              // this made the parent's string state hold a whole object,
              // which Kendo then tried to render as a child and crashed
              // with "Objects are not valid as a React child".
              const opt = e.value as UniDropdownOption | null;
              onChange(opt?.value ?? "");
              if (touched) runValidation(opt?.value ?? "");
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disable}
            filterable={false}
            className={`relative ${
              touched && error ? "gx-invalid" : "gx-valid"
            } ${selectedValue === "" ? "gx-placeholder" : "gx-filled"}`}
            itemRender={(li, itemProps) => {
              const opt = itemProps.dataItem as UniDropdownOption;
              return React.cloneElement(li, {
                ...li.props,
                className: "text-sm px-4 py-2 hover:bg-gray-100",
                children: opt.subLabel ? (
                  <div className="gx-dropdown-option">
                    <span className="gx-dropdown-option-main">
                      {opt.label}
                    </span>
                    <span className="gx-dropdown-option-sub">
                      {opt.subLabel}
                    </span>
                  </div>
                ) : (
                  opt.label
                ),
              });
            }}
            // Controls what shows in the closed/selected box. For two-line
            // options we show only the sub-label (e.g. gxLookupValue);
            // plain string[] options fall back to their single label.
            valueRender={(element, val) => {
              const opt = val as UniDropdownOption | null;
              if (!opt) return element;
              return React.cloneElement(
                element,
                { ...element.props },
                opt.subLabel || opt.label,
              );
            }}
          />

          {/* Info icon inside row when label not floated */}
          {!selectedValue && !focused && InfoIcon}

          <img
            src={`${ICON_BASE_PATH}down_arrow1.svg`}
            alt="Down Arrow"
            width={20}
            height={20}
            className="gx-dropdown-icon-right cursor-pointer"
          />
        </div>

        {touched && error && (
          <p className="gx_input_validationmessage gx-font-regular">{error}</p>
        )}
      </div>
    </div>
  );
};
